import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useApiErrors, useDomainEvent, useEvent, useLazyCommand } from '@soap/modules';
import { ThinkingAnimation } from '../ThinkingAnimation';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { formatTimeDisplay } from '../../utils/timeUtils';
import {
    BRIDGE_STEP_DURATION_MINUTES,
    getPendingSteps,
    InstructionStepKindKey
} from '../InProgressUnitsPanel/lessonStepState';
import { lessonLoadingStateKey, normalizeIdPart } from '../InProgressUnitsPanel/loadingStateKeys';
import type { InstructionStepDto, LessonDetailDto } from '../InProgressUnitsPanel/types';
import { cn } from '../../lib/utils';

export type LessonSessionDialogMode = 'start' | 'continue';

interface LessonSessionDialogProps {
    unitId: string;
    lessonId: string;
    lesson: LessonDetailDto;
    mode: LessonSessionDialogMode;
    onClose: () => void;
    onSuccess?: () => void;
    setLoadingStates?: React.Dispatch<React.SetStateAction<Set<string>>>;
    onApplyOptimistic?: () => void;
    onRevertOptimistic?: () => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

function formatMinutes(minutes: number): string {
    return formatTimeDisplay(minutes / 60);
}

function isContentStep(step: InstructionStepDto): boolean {
    return !step.e251_StepKind || step.e251_StepKind === InstructionStepKindKey.Lesson;
}

function SessionBridgeSteps() {
    return (
        <li>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <p className="font-medium text-gray-900">Session bridge steps</p>
                <ul className="mt-1 space-y-0.5 list-disc pl-4">
                    <li>Wrap Up ({formatMinutes(BRIDGE_STEP_DURATION_MINUTES)})</li>
                    <li>Review (preview for next session)</li>
                </ul>
            </div>
        </li>
    );
}

export function LessonSessionDialog({
    unitId,
    lessonId,
    lesson,
    mode,
    onClose,
    onSuccess,
    setLoadingStates,
    onApplyOptimistic,
    onRevertOptimistic
}: LessonSessionDialogProps) {
    const pendingSteps = useMemo(() => getPendingSteps(lesson), [lesson]);
    const selectableSteps = useMemo(() => pendingSteps.filter(isContentStep), [pendingSteps]);

    const [selectedCount, setSelectedCount] = useState(() =>
        selectableSteps.length > 0 ? selectableSteps.length : 0
    );
    const [viewState, setViewState] = useState(viewStates.ready);
    const [isWaitingForEvent, setIsWaitingForEvent] = useState(false);
    const awaitingE258Ref = React.useRef(false);

    useEffect(() => {
        setSelectedCount(selectableSteps.length > 0 ? selectableSteps.length : 0);
    }, [selectableSteps]);

    const selectedSteps = useMemo(
        () => selectableSteps.slice(0, selectedCount),
        [selectableSteps, selectedCount]
    );

    const isPartialSelection =
        selectableSteps.length > 0 && selectedCount > 0 && selectedCount < selectableSteps.length;

    const totalMinutes = useMemo(() => {
        const base = selectedSteps.reduce((sum, s) => sum + (s.e251_DurationMinutes ?? 0), 0);
        return isPartialSelection ? base + BRIDGE_STEP_DURATION_MINUTES : base;
    }, [selectedSteps, isPartialSelection]);

    const [sendStartLessonCommand] = useLazyCommand('C268v1_StartLesson');
    const triggerCloseContextMenu = useDomainEvent({ eventName: 'close-context-menu' });

    const lessonLoadingKey = lessonLoadingStateKey(unitId, lessonId);
    const clearLessonLoading = useCallback(() => {
        setLoadingStates?.(prev => {
            const next = new Set(prev);
            next.delete(lessonLoadingKey);
            return next;
        });
    }, [lessonLoadingKey, setLoadingStates]);

    useApiErrors(() => {
        if (viewState === viewStates.submitting) {
            setViewState(viewStates.ready);
            setIsWaitingForEvent(false);
        }
        awaitingE258Ref.current = false;
        clearLessonLoading();
        onRevertOptimistic?.();
    });

    useEvent({
        eventName: 'E258v1_LessonStarted',
        onEventReceived: event => {
            const eAny = event as Record<string, unknown>;
            const eLesson = eAny.e258_LessonId ?? eAny.E258_LessonId;
            const eUnit = eAny.e258_UnitId ?? eAny.E258_UnitId;
            const sameLesson = normalizeIdPart(eLesson) === normalizeIdPart(lessonId);
            const sameUnit = normalizeIdPart(eUnit) === normalizeIdPart(unitId);
            if (!awaitingE258Ref.current || !sameLesson || !sameUnit) return;

            awaitingE258Ref.current = false;
            setIsWaitingForEvent(false);
            setViewState(viewStates.ready);
            if (onSuccess) {
                onSuccess();
            } else {
                onClose();
            }
            triggerCloseContextMenu();
        }
    });

    const handleClose = useCallback(() => {
        awaitingE258Ref.current = false;
        setViewState(viewStates.ready);
        setIsWaitingForEvent(false);
        onClose();
        triggerCloseContextMenu();
    }, [onClose, triggerCloseContextMenu]);

    const handleToggleStep = (index: number) => {
        const nextCount = index + 1;
        setSelectedCount(prev => (prev === nextCount ? Math.max(0, nextCount - 1) : nextCount));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedSteps.length === 0) return;

        const selectedStepIds = selectedSteps
            .map(s => s.e251_StepId)
            .filter((id): id is string => !!id);

        if (selectedStepIds.length === 0) return;

        onApplyOptimistic?.();
        setLoadingStates?.(prev => new Set(prev).add(lessonLoadingKey));
        awaitingE258Ref.current = true;
        setViewState(viewStates.submitting);
        setIsWaitingForEvent(true);

        await new Promise(resolve => requestAnimationFrame(resolve));

        try {
            await sendStartLessonCommand({
                C268_UnitId: unitId,
                C268_LessonId: lessonId,
                C268_SelectedStepIds: selectedStepIds
            });
        } catch (error) {
            console.error('Error starting lesson session:', error);
            awaitingE258Ref.current = false;
            setViewState(viewStates.ready);
            setIsWaitingForEvent(false);
            clearLessonLoading();
            onRevertOptimistic?.();
        }
    };

    const title = mode === 'start' ? 'Start session' : 'Continue session';
    const submitLabel = mode === 'start' ? 'Start Session' : 'Continue Session';

    return (
        <Dialog open onOpenChange={open => !open && handleClose()}>
            <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
                {viewState === viewStates.submitting ? (
                    <div className="flex flex-col items-center justify-center px-6 py-12">
                        <ThinkingAnimation message="Preparing your session steps" />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex max-h-[80vh] flex-col">
                        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
                            <h2 className="text-lg font-medium text-gray-900">{title}</h2>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="shrink-0 space-y-3 px-4 pt-4">
                            {selectedCount > 0 && (
                                <p className="text-sm font-medium text-gray-900">
                                    Total session time: {formatMinutes(totalMinutes)}
                                </p>
                            )}

                            <p className="text-sm text-gray-600">
                                Select the steps to cover in this session.
                                <br />
                                Steps must form a contiguous block.
                            </p>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                            {selectableSteps.length === 0 ? (
                                <p className="text-sm text-gray-500">No incomplete steps remain.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {selectableSteps.flatMap((step, index) => {
                                        const checked = index < selectedCount;
                                        const disabled = index > selectedCount;
                                        const minutes = step.e251_DurationMinutes ?? 0;
                                        const stepItem = (
                                            <li key={step.e251_StepId ?? `pending-${index}`}>
                                                <label
                                                    className={cn(
                                                        'flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer',
                                                        checked
                                                            ? 'border-action-blue/40 bg-action-blue/5'
                                                            : 'border-gray-200',
                                                        disabled && 'opacity-50 cursor-not-allowed'
                                                    )}
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        disabled={disabled}
                                                        onCheckedChange={() => handleToggleStep(index)}
                                                        className="mt-0.5"
                                                    />
                                                    <span className="flex-1 min-w-0">
                                                        <span className="block text-sm font-medium text-gray-900">
                                                            {step.e251_Summary?.trim() ||
                                                                `Step ${step.e251_StepNumber ?? index + 1}`}
                                                        </span>
                                                        {minutes > 0 && (
                                                            <span className="text-xs text-gray-500">
                                                                {formatMinutes(minutes)}
                                                            </span>
                                                        )}
                                                    </span>
                                                </label>
                                            </li>
                                        );

                                        if (isPartialSelection && index === selectedCount - 1) {
                                            return [stepItem, <SessionBridgeSteps key="session-bridge-steps" />];
                                        }

                                        return [stepItem];
                                    })}
                                </ul>
                            )}
                        </div>

                        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-4 py-3">
                            <Button type="button" variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={selectedSteps.length === 0 || isWaitingForEvent}
                                className="bg-action-blue hover:bg-action-blue-dark text-white"
                            >
                                {submitLabel}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
